import type { FormRule, FormRuleComparisonOperator, FormRuleCondition } from "./form-rules"

export const FIELD_APPLICABILITY_RULE_ID = "__field_applicability__"

export type LegacyConditionalApplicabilityOperator = Extract<
  FormRuleComparisonOperator,
  "equals" | "not_equals" | "is_filled" | "is_empty" | "checked" | "unchecked"
>

export type ApplicabilityCondition =
  | {
      kind: "field"
      fieldName: string
      operator: LegacyConditionalApplicabilityOperator
      value?: string
    }
  | {
      kind: "context"
      key: string
      operator: FormRuleComparisonOperator
      value?: string
    }

type ApplicabilityField = {
  conditionalOn?: {
    fieldName: string
    operator: LegacyConditionalApplicabilityOperator
    value?: string
  }
  rules?: FormRule[]
}

export type FieldApplicabilityState =
  | {
      kind: "none"
    }
  | {
      kind: "simple"
      source: "conditionalOn" | "rule"
      condition: ApplicabilityCondition
      rule?: FormRule
    }
  | {
      kind: "complex"
      reason: "mixed_sources" | "multiple_rules" | "unsupported_rule"
      ruleCount: number
    }

export function getFieldApplicabilityState(field: ApplicabilityField): FieldApplicabilityState {
  const selfVisibilityRules = getSelfVisibilityRules(field.rules)

  if (field.conditionalOn && selfVisibilityRules.length > 0) {
    return {
      kind: "complex",
      reason: "mixed_sources",
      ruleCount: selfVisibilityRules.length,
    }
  }

  if (field.conditionalOn) {
    return {
      kind: "simple",
      source: "conditionalOn",
      condition: {
        kind: "field",
        fieldName: field.conditionalOn.fieldName,
        operator: field.conditionalOn.operator,
        value: field.conditionalOn.value,
      },
    }
  }

  if (selfVisibilityRules.length === 0) {
    return { kind: "none" }
  }

  if (selfVisibilityRules.length > 1) {
    return {
      kind: "complex",
      reason: "multiple_rules",
      ruleCount: selfVisibilityRules.length,
    }
  }

  const rule = selfVisibilityRules[0]!
  const condition = parseSimpleApplicabilityRule(rule)
  if (!condition) {
    return {
      kind: "complex",
      reason: "unsupported_rule",
      ruleCount: 1,
    }
  }

  return {
    kind: "simple",
    source: "rule",
    condition,
    rule,
  }
}

export function getRulesWithoutSimpleApplicability(field: ApplicabilityField) {
  const state = getFieldApplicabilityState(field)
  if (state.kind === "simple" && state.source === "rule" && state.rule) {
    return (field.rules ?? []).filter((rule) => rule !== state.rule)
  }

  return field.rules ?? []
}

export function getCanonicalApplicabilityRule(field: ApplicabilityField) {
  const state = getFieldApplicabilityState(field)
  if (state.kind !== "simple") {
    return undefined
  }

  if (
    state.source === "rule" &&
    state.rule &&
    isCanonicalApplicabilityRule(state.rule) &&
    state.rule.id === FIELD_APPLICABILITY_RULE_ID
  ) {
    return state.rule
  }

  return buildApplicabilityRule(state.condition)
}

export function normalizeFieldApplicability(field: ApplicabilityField) {
  const canonicalRule = getCanonicalApplicabilityRule(field)
  if (!canonicalRule) {
    return undefined
  }

  const remainingRules = getRulesWithoutSimpleApplicability(field)
  const nextRules = [...remainingRules, canonicalRule]

  return {
    conditionalOn: undefined,
    rules: nextRules.length > 0 ? nextRules : undefined,
  }
}

export function buildApplicabilityRule(condition: ApplicabilityCondition): FormRule {
  return {
    id: FIELD_APPLICABILITY_RULE_ID,
    when: {
      kind: "not",
      condition,
    },
    effects: [{ type: "setVisibility", visible: false }],
  }
}

function getSelfVisibilityRules(rules: FormRule[] | undefined) {
  return (rules ?? []).filter((rule) => isSelfVisibilityRule(rule))
}

function isSelfVisibilityRule(rule: FormRule) {
  return (
    rule.effects.length === 1 &&
    rule.effects[0]?.type === "setVisibility" &&
    rule.effects[0].targetFieldName === undefined
  )
}

function isCanonicalApplicabilityRule(rule: FormRule) {
  const effect = rule.effects[0]
  return (
    isSelfVisibilityRule(rule) &&
    effect?.type === "setVisibility" &&
    effect.visible === false &&
    rule.when.kind === "not" &&
    normalizeLeafApplicabilityCondition(rule.when.condition) !== undefined
  )
}

function parseSimpleApplicabilityRule(rule: FormRule): ApplicabilityCondition | undefined {
  if (!isSelfVisibilityRule(rule)) {
    return undefined
  }

  const effect = rule.effects[0]
  if (!effect || effect.type !== "setVisibility" || effect.visible !== false) {
    return undefined
  }

  if (rule.when.kind === "not") {
    const condition = normalizeLeafApplicabilityCondition(rule.when.condition)
    if (!condition) {
      return undefined
    }

    return condition
  }

  const condition = normalizeLeafApplicabilityCondition(rule.when)
  if (!condition) {
    return undefined
  }

  return invertApplicabilityCondition(condition)
}

function normalizeLeafApplicabilityCondition(
  condition: FormRuleCondition,
): ApplicabilityCondition | undefined {
  if (condition.kind === "field") {
    if (!isLegacyConditionalOperator(condition.operator)) {
      return undefined
    }

    return {
      kind: "field",
      fieldName: condition.fieldName,
      operator: condition.operator,
      value: condition.value,
    }
  }

  if (condition.kind === "context") {
    return {
      kind: "context",
      key: condition.key,
      operator: condition.operator,
      value: condition.value,
    }
  }

  return undefined
}

function invertApplicabilityCondition(
  condition: ApplicabilityCondition,
): ApplicabilityCondition | undefined {
  const operator = invertApplicabilityOperator(condition.operator)
  if (!operator) {
    return undefined
  }

  return {
    ...condition,
    operator,
  } as ApplicabilityCondition
}

function invertApplicabilityOperator(operator: FormRuleComparisonOperator) {
  switch (operator) {
    case "equals":
      return "not_equals" as const
    case "not_equals":
      return "equals" as const
    case "is_filled":
      return "is_empty" as const
    case "is_empty":
      return "is_filled" as const
    case "checked":
      return "unchecked" as const
    case "unchecked":
      return "checked" as const
    case "gt":
      return "lte" as const
    case "gte":
      return "lt" as const
    case "lt":
      return "gte" as const
    case "lte":
      return "gt" as const
  }
}

function isLegacyConditionalOperator(
  operator: FormRuleComparisonOperator,
): operator is LegacyConditionalApplicabilityOperator {
  return ["equals", "not_equals", "is_filled", "is_empty", "checked", "unchecked"].includes(
    operator,
  )
}
